import * as React from 'react';
import { Query } from 'react-apollo';

import type { ApolloError } from 'apollo-client';
import gql from 'graphql-tag';

import { gradePercentageValue } from 'bundles/course-v2/utils/gradePercentageValue';

import type { CourseCertificate } from './types';

type CertificateDataState = {
  loading: boolean;
  error?: ApolloError;
  certificateData?: CourseCertificate;
};

type Props = {
  certificateCode: string;
  children: (data: CertificateDataState) => JSX.Element;
};

export const CertificateQuery = gql`
  query CertificateQuery($code: String!) {
    MembershipsV1Resource(code: $code)
      @rest(
        type: "MembershipsV1Resource"
        path: "memberships.v1/?q=byCode&code={args.code}&showHidden=true&fields=courseId,role,vcMembershipId,vcMemberships.v1(certificateCode,grade,grantedAt),signatureTrackProfiles.v1(firstName,lastName,middleName),courses.v1(name,description,instructorIds,partnerIds,partnerLogo,v1Details,workload),partners.v1(name),instructors.v1(fullName)&includes=courseId,vcMembershipId,courses.v1(instructorIds,partnerIds,signatureTrackProfile),signatureTrackProfile"
        method: "GET"
      ) {
      elements @type(name: "AccomplishmentsMembership") {
        id
        userId
        courseId
      }
      linked @type(name: "LinkedAccomplishmentData") {
        coursesV1 @type(name: "AccomplishmentsCourse") {
          id
          slug
          name
          description
          partnerLogo
          workload
        }
        signatureTrackProfilesV1 @type(name: "AccomplishmentsSignatureTrackProfile") {
          firstName
          lastName
          middleName
        }
        instructorsV1 @type(name: "AccomplishmentsInstructor") {
          fullName
        }
        partnersV1 @type(name: "AccomplishmentsPartner") {
          name
        }
        vcMembershipsV1 @type(name: "AccomplishmentsVCMembership") {
          certificateCode
          grade @type(name: "AccomplishmentsVCGrade") {
            distinctionLevel
            score
          }
          grantedAt
        }
      }
    }
  }
`;

type MetaImagesResponse = {
  url: string;
};

type CertificateImageResponse = {
  url: string;
};

type MembershipResponse = {
  id: string;
  userId: number;
  courseId: string;
};

type CoursesResponse = {
  id: string;
  slug: string;
  name: string;
  description: string;
  partnerLogo: string;
  workload: string;
};

type SignatureTrackProfilesResponse = {
  firstName: string;
  middleName: string;
  lastName: string;
};

type InstructorsResponse = {
  fullName: string;
};

type PartnersResponse = {
  name: string;
};

type VCMembershipsResponse = {
  certificateCode: string;
  grade?: {
    distinctionLevel: string;
    score: number;
  };
  grantedAt: number;
};

type CertificateDataResponse = {
  MetaImagesV1Resource?: {
    elements: MetaImagesResponse[];
  };
  CertificateImageV1Resource?: {
    elements: CertificateImageResponse[];
  };
  MembershipsV1Resource: {
    elements: MembershipResponse[];
    linked: {
      coursesV1: CoursesResponse[];
      signatureTrackProfilesV1: SignatureTrackProfilesResponse[];
      instructorsV1: InstructorsResponse[];
      partnersV1: PartnersResponse[];
      vcMembershipsV1: VCMembershipsResponse[];
    };
  };
};

const marshalResponse = (data: CertificateDataResponse): CourseCertificate => {
  const membershipsResponse = data?.MembershipsV1Resource;
  const membershipsCoreResponse = membershipsResponse?.elements;
  const signatureTrackProfilesResponse = membershipsResponse?.linked.signatureTrackProfilesV1;
  const coursesResponse = membershipsResponse?.linked.coursesV1;
  const partnersResponse = membershipsResponse?.linked.partnersV1;
  const instructorsResponse = membershipsResponse?.linked.instructorsV1;
  const vcMembershipsResponse = membershipsResponse?.linked.vcMembershipsV1;

  const certificateDataResponse = {
    courseId: coursesResponse[0].id,
    courseName: coursesResponse[0].name,
    courseSlug: coursesResponse[0].slug,
    courseDescription: coursesResponse[0].description,
    courseWorkload: coursesResponse[0].workload,
    userId: membershipsCoreResponse[0].userId,
    userName: [
      signatureTrackProfilesResponse[0].firstName,
      signatureTrackProfilesResponse[0].middleName,
      signatureTrackProfilesResponse[0].lastName,
    ]
      .join(' ')
      .replace(/\s+/, ' '),
    primaryPartnerLogo: coursesResponse[0].partnerLogo,
    instructorNames: instructorsResponse.map((i) => i.fullName),
    partnerNames: partnersResponse.map((p) => p.name),
    certificateGrade: vcMembershipsResponse[0].grade
      ? gradePercentageValue({ grade: vcMembershipsResponse[0].grade.score })
      : undefined,
    certificateGrantTime: vcMembershipsResponse[0].grantedAt,
  };

  return certificateDataResponse;
};

const isEmpty = <T extends {}>(arr: T[] | undefined): boolean => {
  if (!arr) {
    return true;
  }
  return arr.length === 0;
};

const CourseCertificateDataProvider: React.FC<Props> = ({ certificateCode, children }) => {
  return (
    <Query<any, any> query={CertificateQuery} variables={{ code: certificateCode }}>
      {({ loading, error, data }) => {
        if (
          isEmpty(data?.MembershipsV1Resource?.elements) ||
          isEmpty(data?.MembershipsV1Resource?.linked?.coursesV1) ||
          isEmpty(data?.MembershipsV1Resource?.linked?.instructorsV1) ||
          isEmpty(data?.MembershipsV1Resource?.linked?.partnersV1) ||
          isEmpty(data?.MembershipsV1Resource?.linked?.vcMembershipsV1) ||
          isEmpty(data?.MembershipsV1Resource?.linked?.signatureTrackProfilesV1)
        ) {
          return children({ loading, error });
        }
        return children({
          loading,
          error,
          certificateData: marshalResponse(data),
        });
      }}
    </Query>
  );
};

export default CourseCertificateDataProvider;
